import React from "react";
import LegalPage, { LegalList } from "@/components/LegalPage";

const EFFECTIVE_DATE = "September 14, 2026";

const SECTIONS = [
  {
    heading: "Introduction",
    body: [
      'This Privacy Policy explains how Ryde Excursion and Tours ("Carivisor", "we", "us", or "our") collects, uses, shares, and protects your personal information when you use the Carivisor website, mobile application, and related services (collectively, the "Services").',
      "Carivisor is a Caribbean travel marketplace that connects travelers with tours, excursions, and experiences offered by independent local providers. By creating an account or using the Services, you agree to the practices described in this Policy.",
    ],
  },
  {
    heading: "Information We Collect",
    body: [
      "We collect information you provide directly, information generated through your use of the Services, and information from third parties that help us operate our platform.",
      <p key="reg" className="font-semibold text-white pt-2">Account &amp; registration information</p>,
      <LegalList
        key="reglist"
        items={[
          "Name and email address used to create and secure your account.",
          "Phone number for account verification, booking updates, and customer support.",
          "Account role (traveler or provider) and profile details you choose to share.",
          "Passwords, which are stored only in encrypted (hashed) form and never in plain text.",
        ]}
      />,
      <p key="pay" className="font-semibold text-white pt-2">Payment information</p>,
      <LegalList
        key="paylist"
        items={[
          "Bookings and payments are processed securely by our payment provider, Stripe, Inc.",
          "Card and banking details are collected and stored directly by Stripe under its own security standards — Carivisor does not store your full card number.",
          "We receive limited transaction data such as booking amount, currency, payment status, and the last four digits of a saved card to support one-tap checkout, refunds, and receipts.",
        ]}
      />,
      <p key="loc" className="font-semibold text-white pt-2">Location data</p>,
      <LegalList
        key="loclist"
        items={[
          "With your permission, we collect device location to show nearby tours, provide directions to meeting points, enable QR check-in at excursions, and improve booking recommendations.",
          "You can enable or disable location sharing at any time through your device or browser settings. Disabling location may limit certain features such as proximity-based search and check-in.",
        ]}
      />,
      <p key="photo" className="font-semibold text-white pt-2">Photos and images</p>,
      <LegalList
        key="photolist"
        items={[
          "Travelers and providers may upload photos for profile pictures, listing galleries, reviews, and to confirm the delivery or completion of an excursion.",
          "Uploaded photos may be visible to other users depending on where they are shared (for example, a public listing or review).",
          "You are responsible for ensuring you have the right to upload any image and that it does not contain sensitive personal information you do not wish to share.",
        ]}
      />,
      <p key="auto" className="font-semibold text-white pt-2">Automatically collected information</p>,
      <LegalList
        key="autolist"
        items={[
          "Device and usage data such as IP address, browser type, operating system, pages viewed, and referring links.",
          "Cookies and similar technologies used to keep you signed in, remember preferences, and measure performance.",
        ]}
      />,
    ],
  },
  {
    heading: "How We Use Your Information",
    body: [
      "We use the information we collect to operate, maintain, and improve the Services, including to:",
      <LegalList
        key="uselist"
        items={[
          "Create and manage your account and authenticate your logins.",
          "Process bookings, payments, refunds, loyalty points, and provider payouts.",
          "Enable QR check-in, meeting-point directions, and location-based discovery.",
          "Send booking confirmations, receipts, service updates, and support responses.",
          "Personalize content, recommendations, and marketing (where permitted).",
          "Detect, prevent, and investigate fraud, abuse, and security incidents.",
          "Comply with legal obligations and enforce our Terms of Service.",
        ]}
      />,
    ],
  },
  {
    heading: "How We Share Your Information",
    body: [
      "We do not sell your personal information. We share it only as necessary to provide the Services:",
      <LegalList
        key="sharelist"
        items={[
          "With tour providers, so they can fulfill and manage the bookings you make with them.",
          "With Stripe and other payment processors to complete transactions securely.",
          "With service providers that support hosting, analytics, email delivery, and customer support, under confidentiality obligations.",
          "When required by law, legal process, or to protect the rights, safety, and property of Carivisor, our users, or the public.",
          "In connection with a merger, acquisition, or sale of assets, subject to this Policy.",
        ]}
      />,
    ],
  },
  {
    heading: "Data Retention",
    body: [
      "We retain your personal information for as long as your account is active or as needed to provide the Services, comply with legal obligations, resolve disputes, and enforce our agreements. When information is no longer required, we securely delete or anonymize it.",
    ],
  },
  {
    heading: "Security",
    body: [
      "We use industry-standard technical and organizational measures to protect your information, including encryption of passwords and secure handling of payment data through Stripe. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    heading: "Your Rights and Choices",
    body: [
      "Depending on your location, you may have the right to access, correct, update, or delete your personal information, object to certain processing, or withdraw consent. You can update most account details directly in your profile, or contact us to make a request.",
      "You may also opt out of marketing emails at any time using the unsubscribe link, and control cookies and location sharing through your device or browser settings.",
    ],
  },
  {
    heading: "Children's Privacy",
    body: [
      "The Services are not directed to children under the age of 16, and we do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can remove it.",
    ],
  },
  {
    heading: "International Users",
    body: [
      "Carivisor operates from the Caribbean and may process and store information in countries that may have different data protection laws than your own. By using the Services, you consent to the transfer of your information as described in this Policy.",
    ],
  },
  {
    heading: "Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. When we make material changes, we will update the effective date above and, where appropriate, provide additional notice. Your continued use of the Services after changes take effect constitutes acceptance of the updated Policy.",
    ],
  },
  {
    heading: "Contact Us",
    body: [
      "If you have questions or requests regarding this Privacy Policy or your personal information, please contact us:",
      <LegalList
        key="contactlist"
        items={[
          "Company: Ryde Excursion and Tours (operating as Carivisor)",
          "Email: info@carivisor.gd",
        ]}
      />,
    ],
  },
];

export default function Privacy() {
  return (
    <LegalPage
      testid="privacy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      intro="Your privacy matters to us. This policy describes what information Carivisor collects, how we use it, and the choices you have."
      effectiveDate={EFFECTIVE_DATE}
      sections={SECTIONS}
      seoTitle="Privacy Policy | Carivisor"
      seoDescription="Learn how Carivisor collects, uses, and protects your personal information, including account, payment, location, and photo data."
      path="/privacy"
    />
  );
}
